import { EMITTER_CONSTANTS } from '@/shared/config';
import { zeroPad, auth, emitter } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import type { EventListItemProps } from './index.types';

/**
 * Tracks the editing conflict and bulk-selection state of one event row in the events tab.
 * Unless the row is an advanced event, it listens to EVENT_ROOM_USER emitter messages to store
 * the event room's users and edit flag, and memoizes whether the row's event id is checked.
 *
 * @param props - Row props carrying isEventAdvance, checkedEventIds, the row data and the event.
 * @returns The conflict state and the checked flag.
 */
export const useEventListItem = ({
  isEventAdvance,
  checkedEventIds,
  data,
  event,
}: Pick<EventListItemProps, 'isEventAdvance' | 'checkedEventIds' | 'data' | 'event'>) => {
  const [state, setState] = useMergeState({
    conflictUsers: [],
    isNotCanEdit: false,
  });

  /**
   * Determines whether the current user may edit this event, given the other users present in its realtime editing room. Builds the room key from the zero-padded study and event friendly ids, takes the last matching room message, sorts its users by arrival time, and keeps editing enabled only while the earliest user matches the signed-in username and the last ten characters of the access token; the sorted users and the flag are written to state.
   *
   * @param msg - Socket room messages to search for this event's room.
   * @returns Nothing.
   */
  const handleCanEditEvent = (msg: any) => {
    const roomUser = zeroPad(event?.study?.friendlyId) + zeroPad(event?.friendlyId);
    /**
     * Picks the socket room entry describing who is currently editing this event. Searches the message list from the end for the last entry whose room matches the composite study and event room key.
     *
     * @returns The matching room entry, or undefined when none matches.
     */
    const ele = _.findLast(msg, (x) => x.room === roomUser);
    if (ele) {
      const { users } = ele;
      /**
       * Orders the users present in the event's editing room by arrival time. Sorts the room entry's user list ascending by its time field, mutating the array in place.
       *
       * @returns Users ordered from earliest to latest arrival.
       */
      const listUserSort = users.sort((a: any, b: any) => a.time - b.time);
      const firstUser = listUserSort.length > 0 ? listUserSort[0] : {};
      let isNotCanEdit = false;
      if (!_.isEmpty(firstUser.username) && !_.isEmpty(firstUser.token)) {
        if (
          firstUser.username === auth.username() &&
          firstUser.token === auth.accessToken().slice(-10)
        ) {
          isNotCanEdit = false;
        }
      }
      setState({ conflictUsers: listUserSort, isNotCanEdit });
    }
  };

  useEffect(() => {
    let eventRoomUserListener: any;
    if (!isEventAdvance) {
      eventRoomUserListener = emitter.addListener(EMITTER_CONSTANTS.EVENT_ROOM_USER, (msg: any) => {
        handleCanEditEvent(msg);
      });
    }

    return (): any => {
      if (eventRoomUserListener) {
        eventRoomUserListener.remove();
      }
    };
  }, [event, checkedEventIds]);

  /**
   * Indicates whether this event row belongs to the current bulk selection. Memoized on the checked event id list, testing whether the row's event id is included in it.
   *
   * @returns True when the row is selected.
   */
  const isEventChecked = useMemo(
    () => _.includes(checkedEventIds, data.eventId),
    [checkedEventIds],
  );

  return { state, isEventChecked };
};
